/**
 * /tracking/deep-link — mobile deep link configuration page (feature #176).
 *
 * Thin TanStack Router route + breadcrumb shell; the actual page lives in
 * @/features/mobile-deep-link/MobileDeepLinkPage. The root-owned
 * `router.tsx` wires this route into the tree during the wire-up cycle;
 * agents must not edit router.tsx directly.
 *
 * `?campaignId=` is reserved so operators can deep-link (meta!) from a
 * campaign detail view straight into this screen pre-filtered to a single
 * campaign config.
 */

import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { MobileDeepLinkPage } from "@/features/mobile-deep-link/MobileDeepLinkPage";
import { isValidCampaignId } from "@/features/mobile-deep-link/api";

interface TrackingDeepLinkSearch {
	campaignId?: string;
}

export const trackingDeepLinkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/deep-link",
	validateSearch: (raw: Record<string, unknown>): TrackingDeepLinkSearch => {
		const v = raw.campaignId;
		if (typeof v === "string" && isValidCampaignId(v)) {
			return { campaignId: v };
		}
		return {};
	},
	component: TrackingDeepLinkRoute,
});

export default trackingDeepLinkRoute;

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function TrackingDeepLinkRoute() {
	const { campaignId } = trackingDeepLinkRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<MobileDeepLinkPage initialCampaignId={campaignId} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Mobile deep links</li>
			</ol>
		</nav>
	);
}

export { TrackingDeepLinkRoute as TrackingDeepLinkPage };
